import React, { useEffect, useMemo, useState } from "react";
import "./CommentExplorer.css";
import CommentEmptyState from "./CommentEmptyState";
import CommentExplorerToolbar from "./CommentExplorerToolbar";
import CommentInspector from "./CommentInspector";
import CommentLanesView from "./CommentLanesView";
import CommentMapView from "./CommentMapView";
import CommentReaderView from "./CommentReaderView";
import CommentRecentView from "./CommentRecentView";
import CommentThreadsView from "./CommentThreadsView";
import { buildCommentTree, findFirstSelectableComment, getRecentComments } from "./commentTreeModel";
import { getCommentDisplayText, getCommentScopeKind } from "./commentText";
import { CommentExplorerMode, CommentExplorerProps, CommentNode, CommentScopeFilter } from "./types";

function commentMatchesQuery(comment: CommentNode, query: string): boolean {
  if (!query) return true;
  const haystack = `${getCommentDisplayText(comment)} ${comment.user?.name || ""}`.toLowerCase();
  return haystack.includes(query);
}

function commentMatchesScope(comment: CommentNode, filter: CommentScopeFilter, currentEditId?: number | null): boolean {
  if (filter === "all") return true;
  return getCommentScopeKind(comment, currentEditId) === filter;
}

function treeHasMatch(comment: CommentNode, query: string, filter: CommentScopeFilter, currentEditId?: number | null): boolean {
  const selfMatches = commentMatchesQuery(comment, query) && commentMatchesScope(comment, filter, currentEditId);
  return selfMatches || comment.replies.some((reply) => treeHasMatch(reply, query, filter, currentEditId));
}

function getInitialCommentExplorerMode(): CommentExplorerMode {
  if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
    return "reader";
  }

  return "overview";
}

const CommentExplorer: React.FC<CommentExplorerProps> = ({
  gistId,
  editId,
  comments,
  loading = false,
  error,
  currentUserId,
  currentUserRole,
  title = "Navigate this Gist discussion",
  subtitle = "Comment Explorer",
  className = "",
  replyUnavailableMessage,
  showOrphanCount = false,
  initialMode,
  selectedCommentId: controlledSelectedCommentId,
  onSelectedCommentChange,
  onReply,
  onSoftRemove,
  onHardDeleteThread,
}) => {
  const [mode, setMode] = useState<CommentExplorerMode>(() => initialMode || getInitialCommentExplorerMode());
  const [internalSelectedCommentId, setInternalSelectedCommentId] = useState<number | null>(null);
  const selectedCommentId = controlledSelectedCommentId ?? internalSelectedCommentId;
  const [searchQuery, setSearchQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState<CommentScopeFilter>("all");

  const tree = useMemo(() => buildCommentTree(comments || []), [comments]);
  const recentComments = useMemo(() => getRecentComments(tree.all), [tree.all]);
  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredRoots = useMemo(
    () => tree.roots.filter((root) => treeHasMatch(root, normalizedSearch, scopeFilter, editId)),
    [tree.roots, normalizedSearch, scopeFilter, editId]
  );

  const filteredRecent = useMemo(
    () => recentComments.filter((comment) => commentMatchesQuery(comment, normalizedSearch) && commentMatchesScope(comment, scopeFilter, editId)),
    [recentComments, normalizedSearch, scopeFilter, editId]
  );

  const selectedComment = selectedCommentId === null ? null : tree.byId.get(selectedCommentId) ?? null;

  useEffect(() => {
    if (initialMode) setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mobileQuery = window.matchMedia("(max-width: 768px)");
    const moveOverviewToReaderOnMobile = () => {
      if (!mobileQuery.matches) return;
      setMode((currentMode) => (currentMode === "overview" ? "reader" : currentMode));
    };

    moveOverviewToReaderOnMobile();

    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", moveOverviewToReaderOnMobile);
      return () => mobileQuery.removeEventListener("change", moveOverviewToReaderOnMobile);
    }

    mobileQuery.addListener(moveOverviewToReaderOnMobile);
    return () => mobileQuery.removeListener(moveOverviewToReaderOnMobile);
  }, []);

  useEffect(() => {
    if (tree.all.length === 0) {
      setInternalSelectedCommentId((previous) => {
        if (previous !== null) onSelectedCommentChange?.(null);
        return null;
      });
      return;
    }

    setInternalSelectedCommentId((previous) => {
      const candidate = controlledSelectedCommentId ?? previous;
      if (candidate !== null && candidate !== undefined && tree.byId.has(candidate)) return candidate;
      const fallbackId = findFirstSelectableComment(tree.roots)?.id ?? null;
      onSelectedCommentChange?.(fallbackId);
      return fallbackId;
    });
  }, [controlledSelectedCommentId, onSelectedCommentChange, tree]);

  const handleSelect = (commentId: number) => {
    setInternalSelectedCommentId(commentId);
    onSelectedCommentChange?.(commentId);
  };

  const handleOpenReader = (commentId: number) => {
    setInternalSelectedCommentId(commentId);
    onSelectedCommentChange?.(commentId);
    setMode("reader");
  };

  const handleOpenMap = (commentId: number) => {
    setInternalSelectedCommentId(commentId);
    onSelectedCommentChange?.(commentId);
    setMode("map");
  };

  return (
    <div className={`comment-explorer ${className}`.trim()} data-gist-id={gistId}>
      <div className="comment-explorer-topline">
        <div>
          <span>{subtitle}</span>
          <h2>{title}</h2>
        </div>
        <div className="comment-explorer-summary-pills">
          <span>{tree.roots.length} threads</span>
          <span>{tree.all.length} comments</span>
          {showOrphanCount && tree.orphans.length > 0 && <span>{tree.orphans.length} orphaned</span>}
        </div>
      </div>

      <CommentExplorerToolbar
        mode={mode}
        searchQuery={searchQuery}
        scopeFilter={scopeFilter}
        onModeChange={setMode}
        onSearchChange={setSearchQuery}
        onScopeFilterChange={setScopeFilter}
      />

      {loading && tree.all.length === 0 ? (
        <CommentEmptyState title="Loading comments…" message="The Comment Explorer is preparing this discussion tree." />
      ) : error && tree.all.length === 0 ? (
        <CommentEmptyState title="Could not load comments" message="Please refresh the Gist or try again later." />
      ) : tree.all.length === 0 ? (
        <CommentEmptyState title="No comments yet" message="Start the first comment above. It will appear here as a navigable thread." />
      ) : filteredRoots.length === 0 && filteredRecent.length === 0 ? (
        <CommentEmptyState title="No comments match this filter" message="Try clearing search or switching back to All comments." />
      ) : (
        <>
          <div className="comment-explorer-layout">
            <div className="comment-explorer-workspace">
              {mode === "overview" && (
                <CommentThreadsView
                  roots={filteredRoots}
                  selectedCommentId={selectedCommentId}
                  currentEditId={editId}
                  onSelect={handleSelect}
                  onOpenReader={handleOpenReader}
                  onOpenMap={handleOpenMap}
                />
              )}

              {mode === "lanes" && (
                <CommentLanesView
                  roots={filteredRoots}
                  byId={tree.byId}
                  selectedComment={selectedComment}
                  currentEditId={editId}
                  onSelect={handleSelect}
                  onOpenReader={handleOpenReader}
                  onOpenMap={handleOpenMap}
                />
              )}

              {mode === "reader" && (
                <CommentReaderView
                  roots={tree.roots}
                  byId={tree.byId}
                  selectedComment={selectedComment}
                  currentEditId={editId}
                  onSelect={handleSelect}
                />
              )}

              {mode === "map" && (
                <CommentMapView
                  roots={filteredRoots}
                  byId={tree.byId}
                  selectedComment={selectedComment}
                  selectedCommentId={selectedCommentId}
                  currentEditId={editId}
                  onSelect={handleSelect}
                />
              )}

              {mode === "recent" && (
                <CommentRecentView
                  comments={filteredRecent}
                  byId={tree.byId}
                  selectedCommentId={selectedCommentId}
                  currentEditId={editId}
                  onSelect={handleSelect}
                  onOpenReader={handleOpenReader}
                />
              )}
            </div>
          </div>

          <CommentInspector
            selectedComment={selectedComment}
            byId={tree.byId}
            currentEditId={editId}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            onSelect={handleSelect}
            onReply={onReply}
            replyUnavailableMessage={replyUnavailableMessage}
            onSoftRemove={onSoftRemove}
            onHardDeleteThread={onHardDeleteThread}
          />
        </>
      )}
    </div>
  );
};

export default CommentExplorer;
